// Author: Akarsh Singh | 2026-09-17 | RFQ wizard read snapshots; never cache writes or permission decisions.
// Author: Akarsh Singh | 2026-09-19 | Added a reference-data tier for the payment-terms
// and preview tabs. Wizard draft sections keep the short 30s window because they back
// editable forms; reference lists (payment terms, tax profiles, port agents, freight
// forwarders, transport modes, packaging, branch addresses) only change from Settings,
// so they hold for 5 minutes. Both tiers are flushed together by invalidateAll(), which
// the token interceptor fires on every mutating request.
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';

interface Entry {
  subject: BehaviorSubject<any>;
  updatedAt: number;
  inFlight?: Promise<any>;
}

// How long a snapshot may be served without going back to the API.
const DRAFT_SECTION_TTL_MS = 30 * 1000;
const REFERENCE_DATA_TTL_MS = 5 * 60 * 1000;
// The map holds both tiers now, so it needs room for more than the 4 wizard sections.
const MAX_ENTRIES = 40;

@Injectable({ providedIn: 'root' })
export class ServiceRfqWizardSWRService {
  private cache = new Map<string, Entry>();
  private epoch = 0;
  constructor(private cookies: CookieService) {}

  keyOf(serviceRfqId: unknown, section: string): string {
    return JSON.stringify([...this.identity(), String(serviceRfqId), section]);
  }

  /**
   * Key for a reference list that belongs to the signed-in user rather than to one RFQ,
   * e.g. refKeyOf('port-agents', userId). Scoped to the same identity cookies as keyOf so
   * a different login can never read the previous user's lists out of the cache.
   */
  refKeyOf(name: string, ...parts: unknown[]): string {
    return JSON.stringify([...this.identity(), 'ref', name, ...parts.map(String)]);
  }

  private identity(): string[] {
    return ['userId', 'userRole', 'roleId', 'companyId', 'clientId', 'tokenId', 'tokenHash']
      .map(name => this.cookies.get(name));
  }

  private entry(key: string): Entry {
    let entry = this.cache.get(key);
    if (!entry) {
      if (this.cache.size >= MAX_ENTRIES) this.cache.delete(this.cache.keys().next().value);
      entry = { subject: new BehaviorSubject(null), updatedAt: 0 };
      this.cache.set(key, entry);
    }
    return entry;
  }

  getCached(key: string, ttlMs: number = DRAFT_SECTION_TTL_MS): any {
    const entry = this.entry(key);
    return Date.now() - entry.updatedAt < ttlMs ? this.clone(entry.subject.value) : null;
  }

  /**
   * Cache-first read for reference lists: serves a snapshot that is still inside its TTL
   * and otherwise fetches. Concurrent callers share the single in-flight request, which is
   * what collapses the repeated dropdown loads across the four wizard tabs.
   */
  async read(key: string, fetcher: () => Promise<any>, ttlMs: number = REFERENCE_DATA_TTL_MS): Promise<any> {
    const cached = this.getCached(key, ttlMs);
    if (cached !== null) return cached;

    const fresh = await this.refresh(key, fetcher);
    // refresh() discards its result when a write invalidates the store mid-flight. For a
    // cache that is the right call, but a caller asking to read must not be handed
    // undefined and quietly render an empty list, so fetch once more against the new
    // generation. One retry only: another invalidation is a genuinely concurrent write and
    // the caller's own reload will cover it.
    return fresh === undefined ? this.refresh(key, fetcher) : fresh;
  }

  refresh(key: string, fetcher: () => Promise<any>): Promise<any> {
    const entry = this.entry(key);
    if (entry.inFlight) return entry.inFlight.then(data => this.clone(data));
    const epoch = this.epoch;
    const task = Promise.resolve().then(fetcher).then(data => {
      if (epoch !== this.epoch || this.cache.get(key) !== entry) return undefined;
      entry.updatedAt = Date.now();
      entry.subject.next(this.clone(data));
      return data;
    }).finally(() => {
      if (entry.inFlight === task) entry.inFlight = undefined;
    });
    entry.inFlight = task;
    return task.then(data => this.clone(data));
  }

  invalidateAll(): void {
    this.epoch++;
    this.cache.clear();
  }

  isMutation(url: string, method: string): boolean {
    if (/^(GET|HEAD|OPTIONS)$/i.test(method)) return false;
    const path = url.split('?')[0];
    if (!/\/(services|rfq|users|settings|supplier|buyer|serviceCategory)\//i.test(path)) return false;
    return !/^(get|fetch|search|filter|check|verify|generate|export)/i.test(path.split('/').pop() || '');
  }

  private clone(data: any): any {
    return data == null ? data : JSON.parse(JSON.stringify(data));
  }
}
